import type { RacemapEvent } from '@racemap/utilities/types/events';
import * as Comlink from 'comlink';
import type { Immutable } from 'immer';
import type { ManagedTracker } from '../store/trackers/trackers_reducers';

import { type TrackerTag, TrackerTagSchema } from '@racemap/sdk/schema/trackerTags';
import { TrackerMessageSchema, TrackerOverviewSchema } from '@racemap/sdk/schema/trackers';
import { z } from 'zod';
import {
  Filters,
  MessageFilters,
  getActivationState,
  getBatteryFilterCategory,
  getEmptyFilterCounts,
  getLastAtFilter,
  getLocationState,
  getOnlineStateValue,
  hasNoNewMessage,
} from '../components/TrackerManager/Filter/FilterTreeFunctions';
import { getUserFilterCounts } from './filtertree-preparation-helper';

const MessageCountsSchema = z.object({
  errorCount: z.number(),
  pendingCount: z.number(),
  scheduledCount: z.number(),
});
const ManagmentTrackerSchema = TrackerOverviewSchema.extend({
  messagesCounts: MessageCountsSchema,
  messages: z.array(
    TrackerMessageSchema.extend({
      payload: z.any(),
    }),
  ),
});

export class FilterTreePreparation {
  calculateFilterCounts(options: string): Map<Filters, Map<string, number>> {
    // workaround to tranfer object ids. the mechanic of comlink to transfer is the mechanic
    // of structured clone algorithm. this algorithm does not support object ids. it converts
    // objects to empty objects. so we have to convert the object ids to strings before transfering
    // https://github.com/GoogleChromeLabs/comlink#comlinktransfervalue-transferables-and-comlinkproxyvalue
    // https://github.com/Automattic/mongoose/issues/13341
    const {
      trackers,
      events,
      tags,
      filteredUserIds,
    }: {
      trackers: Array<Immutable<ManagedTracker>>;
      events: Array<RacemapEvent>;
      tags: Array<TrackerTag>;
      filteredUserIds: Array<string>;
    } = JSON.parse(options);

    const filterCounts = getEmptyFilterCounts();
    const parsedTags = tags.map((t) => TrackerTagSchema.parse(t));
    filterCounts.set(Filters.Events, new Map(events.map((e) => [e.id, 0])));
    filterCounts.set(Filters.Tags, new Map(parsedTags.map((t) => [t.id.toHexString(), 0])));

    for (const trackerRaw of trackers) {
      const tracker = ManagmentTrackerSchema.parse(trackerRaw);

      // TODO: make a generic function for all categories
      // events filter
      if (tracker.eventIds != null && tracker.eventIds.length > 0) {
        const events = filterCounts.get(Filters.Events);
        for (const eventId of tracker.eventIds) {
          const eventCount = events?.get(eventId.toHexString());
          if (eventCount == null) continue;
          events?.set(eventId.toHexString(), eventCount + 1);
        }
      }

      // tags filter
      const filterTags = filterCounts.get(Filters.Tags);
      if (filterTags != null) {
        for (const tag of parsedTags) {
          const tagCount = filterTags.get(tag.id.toHexString());
          if (tagCount == null) continue;
          if (tag.trackerIds.some((tId) => tId.equals(tracker.id)))
            filterTags.set(tag.id.toHexString(), tagCount + 1);
        }
      }

      // battery
      if (tracker.state.battery != null) {
        const batteryFilterCounts = filterCounts.get(Filters.Battery);
        const batteryFilterCategory = getBatteryFilterCategory(tracker.state.battery);
        const batteryCount = batteryFilterCounts?.get(batteryFilterCategory) || 0;

        batteryFilterCounts?.set(batteryFilterCategory, batteryCount + 1);
      }

      // trackerType
      const trackerTypeFilterCounts = filterCounts.get(Filters.TrackerType);
      const trackerTypeCount = trackerTypeFilterCounts?.get(tracker.trackerType) || 0;
      trackerTypeFilterCounts?.set(tracker.trackerType, trackerTypeCount + 1);

      // online
      const onlineFilterCounts = filterCounts.get(Filters.Online);
      const onlineFilterCategory = getOnlineStateValue(tracker.state);
      const onlineStateCount = onlineFilterCounts?.get(onlineFilterCategory) || 0;
      onlineFilterCounts?.set(onlineFilterCategory, onlineStateCount + 1);

      // messages
      if (tracker.messages != null) {
        const messagesFilterCounts = filterCounts.get(Filters.Messages);

        const trackerhasNoNewMessage = hasNoNewMessage(tracker.messages);

        const noNewMessageCount = messagesFilterCounts?.get(MessageFilters.NO_NEW_MESSAGES) || 0;
        const withErrorCount = messagesFilterCounts?.get(MessageFilters.WITH_ERROR_MESSAGES) || 0;
        const withPendingCount =
          messagesFilterCounts?.get(MessageFilters.WITH_PENDING_MESSAGES) || 0;
        const withScheduledCount =
          messagesFilterCounts?.get(MessageFilters.WITH_SCHEDULED_MESSAGES) || 0;

        messagesFilterCounts?.set(
          MessageFilters.NO_NEW_MESSAGES,
          noNewMessageCount + (trackerhasNoNewMessage ? 1 : 0),
        );
        messagesFilterCounts?.set(
          MessageFilters.WITH_ERROR_MESSAGES,
          withErrorCount + (tracker.messagesCounts?.errorCount > 0 ? 1 : 0),
        );
        messagesFilterCounts?.set(
          MessageFilters.WITH_PENDING_MESSAGES,
          withPendingCount + (tracker.messagesCounts?.pendingCount > 0 ? 1 : 0),
        );
        messagesFilterCounts?.set(
          MessageFilters.WITH_SCHEDULED_MESSAGES,
          withScheduledCount + (tracker.messagesCounts?.scheduledCount ? 1 : 0),
        );
      } else {
        const messagesFilterCounts = filterCounts.get(Filters.Messages);
        const noNewMessageCount = messagesFilterCounts?.get(MessageFilters.NO_NEW_MESSAGES) || 0;
        messagesFilterCounts?.set(MessageFilters.NO_NEW_MESSAGES, noNewMessageCount + 1);
      }

      // location
      if (tracker.lastGeo != null) {
        const locationFilterCounts = filterCounts.get(Filters.Location);
        const locationFilterCategory = getLocationState(tracker);
        const locationCount = locationFilterCounts?.get(locationFilterCategory) || 0;

        locationFilterCounts?.set(locationFilterCategory, locationCount + 1);
      }

      // activation
      if (tracker.activation != null) {
        const activationFilterCounts = filterCounts.get(Filters.Activation);
        const activationFilterCategory = getActivationState(tracker.activation);
        const activationCount = activationFilterCounts?.get(activationFilterCategory) || 0;

        activationFilterCounts?.set(activationFilterCategory, activationCount + 1);
      }

      // logistics
      if (tracker.logistics != null) {
        const logisticsFilterCounts = filterCounts.get(Filters.Logistics);
        const logisticsFilterCategory = tracker.logistics;
        const logisticsCount = logisticsFilterCounts?.get(tracker.logistics) || 0;

        logisticsFilterCounts?.set(logisticsFilterCategory, logisticsCount + 1);
      }

      // lastDataAtq
      if (tracker.state.lastDataAt != null) {
        const updatedAtFilterCounts = filterCounts.get(Filters.LastDataAt);
        const updatedAtFilterCategory = getLastAtFilter(tracker.state.lastDataAt);
        const updatedAtCount = updatedAtFilterCounts?.get(updatedAtFilterCategory) || 0;

        updatedAtFilterCounts?.set(updatedAtFilterCategory, updatedAtCount + 1);
      }

      // lastConnectedAt
      if (tracker.state.lastConnectedAt != null) {
        const lastConnectedAtFilterCounts = filterCounts.get(Filters.LastConnectedAt);
        const lastConnectedAtFilterCategory = getLastAtFilter(tracker.state.lastConnectedAt);
        const lastConnectedAtCount =
          lastConnectedAtFilterCounts?.get(lastConnectedAtFilterCategory) || 0;

        lastConnectedAtFilterCounts?.set(lastConnectedAtFilterCategory, lastConnectedAtCount + 1);
      }

      // lastDisconnectedAt
      if (tracker.state.lastDisconnectedAt != null) {
        const lastDisconnectedAtFilterCounts = filterCounts.get(Filters.LastDisconnectedAt);
        const lastDisconnectedAtFilterCategory = getLastAtFilter(tracker.state.lastDisconnectedAt);
        const lastDisconnectedAtCount =
          lastDisconnectedAtFilterCounts?.get(lastDisconnectedAtFilterCategory) || 0;

        lastDisconnectedAtFilterCounts?.set(
          lastDisconnectedAtFilterCategory,
          lastDisconnectedAtCount + 1,
        );
      }

      // users
      if (tracker.owner !== null) {
        filterCounts.set(
          Filters.Users,
          getUserFilterCounts(filterCounts, tracker, filteredUserIds),
        );
      }
    }
    return filterCounts;
  }
}

const calculateFilterCounts: FilterTreePreparation = new FilterTreePreparation();

Comlink.expose(calculateFilterCounts);
